import React, { useState } from 'react'
import { useAtom } from 'jotai'
import Classnames from 'classnames'

// helper functions
import { returnValue } from '@/utils/op-types/helperFunctions'

// store
import {
  isBuilder,
  manifestStatic,
  pageContentStatic,
  manifestDesign,
  pageContentDesign,
  manifestBlocks,
  pageContentBlocks,
} from '@/utils/op-types/store'

// block renderer
import BlockRenderer from '@/components/optypes/frontend/BlockRenderer'

// block builder tools
import StaticContentWrapper from '@/components/optypes/frontend/StaticContentWrapper'
import AddBlockBuilder from '@/components/optypes/frontend/AddBlockBuilder'

// components
import BlocksHeader from '@/components/optypes/frontend/CenterScroller/BlocksHeader'

import ReactPlayer from 'react-player'

// blocks
import Header from '@/components/optypes/frontend/SideScroller/blocks/Header'
import InputBlock from '@/components/optypes/frontend/SideScroller/blocks/InputBlock'
import ButtonBlock from '@/components/optypes/frontend/SideScroller/blocks/ButtonBlock'
import MultiChoiceBlock from '@/components/optypes/frontend/SideScroller/blocks/MultiChoiceBlock'
import LinkBlock from '@/components/optypes/frontend/SideScroller/blocks/LinkBlock'

import dynamic from 'next/dynamic'

// drag and drop context wrapper
import DragWrapper from '@/components/optypes/headlesss-components/DragWrapper'
import OnepagerFooter from '@/components/optypes/headlesss-components/OnepagerFooter'

const CardsBlock = dynamic(
  () => import('@/components/optypes/frontend/SideScroller/blocks/CardsBlock'),
  {
    ssr: false,
  }
)

const TodosBlock = dynamic(
  () => import('@/components/optypes/frontend/SideScroller/blocks/TodosBlock'),
  {
    ssr: false,
  }
)

const Scrollbars = dynamic(() => import('react-custom-scrollbars'), {
  ssr: false,
})

const Components = {
  header: Header,
  inputblock: InputBlock,
  cardblock: CardsBlock,
  todosblock: TodosBlock,
  ctablock: ButtonBlock,
  multichoiceblock: MultiChoiceBlock,
  linkblock: LinkBlock,
}

const SideScroller = () => {
  const [builder] = useAtom(isBuilder)
  // manifest settings
  const [opStatic] = useAtom(manifestStatic)
  const [opDesign] = useAtom(manifestDesign)
  const [opBlocks] = useAtom(manifestBlocks)
  // custom page settings
  const [pageStatic] = useAtom(pageContentStatic)
  const [pageDesign] = useAtom(pageContentDesign)
  const [pageBlocks] = useAtom(pageContentBlocks)

  const [fullScreen, setFullScreen] = useState(false)

  return (
    <div
      className="pb-12 opr-mainarea"
      style={{
        backgroundColor: returnValue('pagebg', opDesign, pageDesign),
      }}
    >
      {/* HEADER */}
      <div
        className={Classnames(
          'opr-mainheader border-b-2 border-black border-opacity-25 relative z-10'
        )}
        style={{
          backgroundColor: returnValue('bgcolor', opDesign, pageDesign),
          color: returnValue('maintextcolor', opDesign, pageDesign),
          // boxShadow:
          //   '10px 0 11px 4px rgba(0,0,0,0.08), 5px 0 7px 4px rgba(0,0,0,0.1)',
        }}
      >
        {/* background image */}
        {returnValue('bgimgurl', opDesign, pageDesign) && (
          <div
            className="absolute top-0 left-0 z-0 h-full w-full  bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url(${returnValue(
                'bgimgurl',
                opDesign,
                pageDesign
              )})`,
              opacity: returnValue('bgimgopacity', opDesign, pageDesign),
            }}
          ></div>
        )}

        {/* <CopyHeader /> */}

        <div className="container max-w-6xl mx-auto">
          <div className=" flex items-center relative z-10 pt-12 pb-28">
            {/* headline / subheadline */}
            <div
              className={Classnames('opr-header-text', {
                'w-3/4 pr-12':
                  !returnValue('videourl', opStatic, pageStatic) &&
                  !returnValue('imgurl', opStatic, pageStatic),
                'w-1/2 pr-12':
                  returnValue('videourl', opStatic, pageStatic) ||
                  returnValue('imgurl', opStatic, pageStatic),
              })}
            >
              <h1
                className={Classnames('font-bold text-6xl', {
                  appTitleItem:
                    returnValue('herofont', opDesign, pageDesign) == 'sans',
                })}
                style={{
                  color: returnValue('headlinetextcolor', opDesign, pageDesign),
                }}
              >
                <StaticContentWrapper contentKey="headline">
                  {returnValue('headline', opStatic, pageStatic)}
                </StaticContentWrapper>
              </h1>
              <h3 className="mt-4 mb-6 text-xl font-medium">
                <StaticContentWrapper contentKey="subheadline">
                  {returnValue('subheadline', opStatic, pageStatic)}
                </StaticContentWrapper>
              </h3>
            </div>

            <div
              className={Classnames({
                'w-1/2':
                  returnValue('videourl', opStatic, pageStatic) ||
                  returnValue('imgurl', opStatic, pageStatic),
              })}
            >
              <div className="opr-header-video">
                {/* video */}
                {returnValue('videourl', opStatic, pageStatic) && (
                  <div className="player-wrapper rounded shadow-lg overflow-hidden mb-8">
                    <ReactPlayer
                      controls={false}
                      playing={false}
                      width="100%"
                      height="100%"
                      className="react-player"
                      url={returnValue('videourl', opStatic, pageStatic)}
                    />
                  </div>
                )}

                {/* img */}
                {returnValue('imgurl', opStatic, pageStatic) && (
                  <img
                    src={returnValue('imgurl', opStatic, pageStatic)}
                    alt=""
                    className="rounded shadow-lg mb-8"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* side scroller content blocks */}
      <div
        className={Classnames(
          'container max-w-6xl mx-auto bg-white shadow rounded -mt-24 relative z-10 overflow-hidden opr-container',
          {}
        )}
        style={{
          backgroundColor: returnValue('blockbgcolor', opDesign, pageDesign),
          color: returnValue('blocktextcolor', opDesign, pageDesign),
        }}
      >
        <BlocksHeader />

        <div className="p-12 space-y-4">
          <DragWrapper>
            {pageBlocks?.map((block, index) => (
              <BlockRenderer
                key={index}
                index={index}
                Components={Components}
                block={block}
              />
            ))}
          </DragWrapper>

          {/* add block button builder */}
          {builder && <AddBlockBuilder btnText="Add Block" />}
        </div>
      </div>

      <div
        className="py-12"
        style={{
          color: returnValue('maintextcolor', opDesign, pageDesign),
        }}
      >
        <OnepagerFooter />
      </div>
    </div>
  )
}

export default SideScroller
